/**
 * Candidate de-duplication triage.
 *
 * Lists groups of likely duplicates (same email / same phone / same name)
 * and lets the recruiter pick which candidate to keep (survivor) — merging
 * the other(s) into it. Applications, notes, ratings, online links,
 * portfolio etc. all follow the survivor automatically.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Users, Loader2, GitMerge, Mail, Phone, User, AlertCircle } from "lucide-react";

const REASON_LABEL = {
    same_email: { label: "Same email", icon: Mail, tone: "text-rose-700 border-rose-300 bg-rose-50" },
    same_phone: { label: "Same phone", icon: Phone, tone: "text-amber-700 border-amber-300 bg-amber-50" },
    same_name:  { label: "Similar name", icon: User,  tone: "text-blue-700 border-blue-300 bg-blue-50" },
};

function shortDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch { return ""; }
}

export default function CandidateDuplicates() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyKey, setBusyKey] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/candidate-hygiene/duplicates");
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const doMerge = async (group, survivorId, loserId) => {
        if (!window.confirm("Merge these two candidates? The loser will be deleted; all applications, notes, links and ratings follow the survivor.")) return;
        const key = `${group.reason}-${group.key}-${loserId}`;
        setBusyKey(key);
        try {
            await api.post(`/candidates/${survivorId}/merge`, { loser_id: loserId });
            toast.success("Merged");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusyKey(null); }
    };

    return (
        <div className="max-w-[1600px] mx-auto p-4 sm:p-5 lg:p-6 space-y-5" data-testid="dedupe-root">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb inline-flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" /> Candidate hygiene
                </div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">De-duplicate candidates</h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                    Groups below share an email, phone, or similar name. Pick the canonical record (keep)
                    and merge the rest into it — applications, notes, ratings, online links and portfolio
                    items all follow the survivor.
                </p>
            </div>

            {loading ? (
                <div className="text-center py-16 text-sm text-muted-foreground inline-flex items-center gap-2 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Scanning candidates…
                </div>
            ) : !data || (data.groups || []).length === 0 ? (
                <div className="border border-dashed border-border bg-white p-12 text-center" data-testid="dedupe-empty">
                    <AlertCircle className="w-6 h-6 mx-auto text-emerald-600 mb-2" />
                    <div className="font-display font-bold">No duplicates found</div>
                    <div className="text-xs text-muted-foreground mt-1">Scanned {data?.scanned ?? 0} candidates.</div>
                </div>
            ) : (
                <div className="space-y-4" data-testid="dedupe-groups">
                    {data.groups.map((g, gi) => {
                        const meta = REASON_LABEL[g.reason] || REASON_LABEL.same_name;
                        const Icon = meta.icon;
                        return (
                            <section key={`${g.reason}-${gi}`} className="border border-border bg-white" data-testid={`dedupe-group-${gi}`}>
                                <header className="px-3 sm:px-4 py-2.5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                                    <div className="inline-flex items-center gap-2 flex-wrap">
                                        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${meta.tone} inline-flex items-center gap-1`}>
                                            <Icon className="w-3 h-3" /> {meta.label}
                                        </span>
                                        <span className="text-xs font-mono text-muted-foreground">{g.key}</span>
                                        <span className="text-[10px] font-mono text-muted-foreground">· {g.candidates.length} records</span>
                                    </div>
                                </header>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-xs">
                                        <thead className="bg-secondary text-foreground/70">
                                            <tr>
                                                <th className="text-left px-3 py-1.5 font-mono uppercase tracking-widest">Name</th>
                                                <th className="text-left px-3 py-1.5 font-mono uppercase tracking-widest">Email</th>
                                                <th className="text-left px-3 py-1.5 font-mono uppercase tracking-widest">Phone</th>
                                                <th className="text-left px-3 py-1.5 font-mono uppercase tracking-widest">Title</th>
                                                <th className="text-left px-3 py-1.5 font-mono uppercase tracking-widest">Created</th>
                                                <th className="text-right px-3 py-1.5 font-mono uppercase tracking-widest">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {g.candidates.map((c, ci) => {
                                                const isOldest = ci === 0;
                                                return (
                                                    <tr key={c.id} className="border-t border-border" data-testid={`dedupe-row-${c.id}`}>
                                                        <td className="px-3 py-2">
                                                            <Link to={`/candidates/${c.id}`} target="_blank" className="text-ikb hover:underline font-medium">
                                                                {c.name || "—"}
                                                            </Link>
                                                            {isOldest && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-emerald-700 border border-emerald-300 bg-emerald-50 px-1 py-0.5">Oldest</span>}
                                                        </td>
                                                        <td className="px-3 py-2">{c.email || <span className="text-muted-foreground">—</span>}</td>
                                                        <td className="px-3 py-2">{c.phone || <span className="text-muted-foreground">—</span>}</td>
                                                        <td className="px-3 py-2">{c.current_title || <span className="text-muted-foreground">—</span>}</td>
                                                        <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">{shortDate(c.created_at)}</td>
                                                        <td className="px-3 py-2 text-right">
                                                            {!isOldest && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="rounded-none h-7 text-[10px] font-mono uppercase tracking-widest"
                                                                    disabled={busyKey !== null}
                                                                    onClick={() => doMerge(g, g.candidates[0].id, c.id)}
                                                                    data-testid={`dedupe-merge-${c.id}`}
                                                                >
                                                                    <GitMerge className="w-3 h-3 mr-1" />
                                                                    {busyKey === `${g.reason}-${g.key}-${c.id}` ? "Merging…" : "Merge into oldest"}
                                                                </Button>
                                                            )}
                                                            {isOldest && <span className="text-[10px] font-mono text-muted-foreground">Survivor</span>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
